import { useMemo, useState } from "react";

import type { FilterDefinition, FilterValue, SaveFilterResult } from "@/ds";
import { useSessionSavedFilters } from "@/shared/hooks";
import {
  DEFAULT_FINANCEIRO_OBRAS_SORT,
  EMPTY_FINANCEIRO_OBRAS_FILTERS,
} from "@/modules/financeiro-obras";
import type { FinanceiroObrasFilters, FinanceiroObrasSort } from "@/modules/financeiro-obras";
import { PERIOD_COLUMN, SAVED_FILTERS_SCREEN } from "../financeiro-obras.constants";
import {
  fromSavedValue,
  periodoToDatas,
  toPanelValue,
  toSavedValue,
} from "../financeiro-obras.utils";
import type { FinanceiroObrasViewMode } from "../types-financeiro-obras";

/**
 * Estado de tela da listagem: visão, busca, filtros, ordenação e filtros salvos. A consulta em si
 * (paginação, atraso da busca, "mostrar mais") é do Model em `@/modules/financeiro-obras`.
 */
export function useFinanceiroObrasCriterios(isMobile: boolean, filterColumns: FilterDefinition[]) {
  const [view, setView] = useState<FinanceiroObrasViewMode>("lista");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<FinanceiroObrasFilters>(EMPTY_FINANCEIRO_OBRAS_FILTERS);
  const [sort, setSort] = useState<FinanceiroObrasSort | null>(null);

  const { savedFilters, saveFilter, deleteFilter, applySavedFilter } =
    useSessionSavedFilters(SAVED_FILTERS_SCREEN);

  const panelKeys = useMemo(() => filterColumns.map((column) => column.key), [filterColumns]);
  const panelValue = useMemo<FilterValue>(
    () => Object.fromEntries(panelKeys.map((key) => [key, toPanelValue(filters)[key] ?? []])),
    [panelKeys, filters],
  );

  /** O painel conhece só as próprias colunas; os demais critérios do conjunto ficam como estão. */
  const panelSavedFilters = useMemo(
    () =>
      savedFilters.map((saved) => ({
        ...saved,
        value: Object.fromEntries(
          panelKeys.map((key) => [key, toPanelValue(fromSavedValue(saved.value))[key] ?? []]),
        ),
      })),
    [savedFilters, panelKeys],
  );

  function applyPanel(value: FilterValue) {
    const [dataInicio, dataFim] = periodoToDatas(value[PERIOD_COLUMN] ?? []);

    setFilters((current) => ({
      ...current,
      embarcacaoId: value.embarcacaoId ?? [],
      condicao: value.condicao ?? [],
      dataInicio,
      dataFim,
      ...(isMobile
        ? {
            statusFinanceiro: value.statusFinanceiro ?? [],
            tipoObra: value.tipoObra ?? [],
          }
        : {}),
    }));
  }

  /** O filtro salvo guarda o conjunto completo (status, período, embarcação e tipo). */
  function handleSaveFilter(name: string, value: FilterValue): SaveFilterResult {
    const { [PERIOD_COLUMN]: periodo, ...rest } = value;
    const [dataInicio, dataFim] = periodoToDatas(periodo ?? []);

    return saveFilter(name, {
      ...toSavedValue(filters),
      ...rest,
      ...(periodo
        ? {
            dataInicio: dataInicio ? [dataInicio] : [],
            dataFim: dataFim ? [dataFim] : [],
          }
        : {}),
    });
  }

  /** Recupera o conjunto salvo inteiro, inclusive o que fica fora do painel. */
  function handleApplySavedFilter(id: string) {
    applySavedFilter(id, (value) => setFilters(fromSavedValue(value)));
  }

  return {
    view,
    changeView: setView,
    search,
    changeSearch: setSearch,
    filters,
    sort,
    appliedSort: sort ?? DEFAULT_FINANCEIRO_OBRAS_SORT,
    changeSort: setSort,
    panelValue,
    panelSavedFilters,
    applyPanel,
    saveFilter: handleSaveFilter,
    applySavedFilter: handleApplySavedFilter,
    deleteSavedFilter: deleteFilter,
    changeStatus: (status: string[]) =>
      setFilters((current) => ({ ...current, statusFinanceiro: status })),
    changeTipo: (tipo: string[]) => setFilters((current) => ({ ...current, tipoObra: tipo })),
    clearSearch: () => setSearch(""),
    clearFilters: () => setFilters(EMPTY_FINANCEIRO_OBRAS_FILTERS),
  };
}
